<template>
  <b-container class="create">
    <b-form @submit.prevent="submit">
      <div class="float-right">
        <b-button variant="default" @click="cancel">Cancel</b-button>
        <b-button variant="info" type="submit" :disabled="saveLoading">
          <template v-if="!saveLoading">Save</template>
          <template v-else>
            <b-spinner small />
            <span>Saving...</span>
          </template>
        </b-button>
      </div>

      <h2>{{ title }}</h2>

      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Society Name:"
            label-class="font-weight-bold"
            :invalid-feedback="nameInvalidFeedback"
          >
            <b-form-input
              type="text"
              placeholder="Name"
              v-model="$v.society.name.$model"
              :state="validateState('name')"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="URL Code:"
            label-class="font-weight-bold"
            :invalid-feedback="urlCodeInvalidFeedback"
          >
            <b-form-input
              type="text"
              placeholder="Code"
              v-model="$v.society.url_code.$model"
              :state="validateState('url_code')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            sty
            class="without-outline"
            label-cols-horizontal
            label-cols="3"
            label="Specialties:"
            label-class="font-weight-bold"
          >
            <b-card no-body>
              <b-card-body>
                <b-row
                  v-for="s in society.specialties"
                  :key="s.id"
                  class="mb-2"
                >
                  <b-col sm="6">{{ s.code }} - {{ s.title }}</b-col>
                  <b-col sm="4">
                    <b-form-input
                      v-model="s.sort_order"
                      type="number"
                      placeholder="Sort order..."
                    />
                  </b-col>
                  <b-col sm="2">
                    <b-btn
                      size="sm"
                      variant="danger"
                      @click="deleteSpecialty(s)"
                    >
                      x
                    </b-btn>
                  </b-col>
                </b-row>
              </b-card-body>
              <b-card-footer>
                <label>Add Specialty:</label>
                <b-form-select
                  v-model="add_specialty_id"
                  :options="specialtyOptions"
                  @change="addSpecialty"
                />
              </b-card-footer>
            </b-card>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            class="without-outline"
            label-cols-horizontal
            label-cols="3"
            label="Society Email Signature:"
            label-class="font-weight-bold"
            :invalid-feedback="textFieldInvalidFeedback"
            description="Use the <br> element to create a line break. Other allowed tags: em,strong,cite,blockquote,code,ul,ol,li,dl,dt,dd,h1,h2,h3,h4,h5,h6,span,*[style]"
          >
            <b-form-textarea
              :rows="4"
              placeholder="Society Email Signature..."
              v-model="$v.society.society_email_signature.$model"
              :state="validateState('society_email_signature')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Is Out of Partners List:"
            label-class="font-weight-bold"
          >
            <div class="d-flex align-items-center h-100">
              <b-form-checkbox
                v-model="society.is_out_of_partners_list"
                :value="1"
                :unchecked-value="0"
              />
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-3">
        <b-col sm="12">
          <society-logo-input
            :log-file-input="$v.logoFile.$model"
            :logo-name-input="society.logo"
            :logo-file-state="validateLogoFileState()"
            :logo-file="logoFile"
            :logo-src="logoSrc"
            :logo-file-invalid-feedback="logoFileInvalidFeedback"
            @input-logo-file="$v.logoFile.$model = $event"
            @change-logo-file="onFileChange"
            @input-logo-name="society.logo = $event"
          />
        </b-col>
      </b-row>
      <slot type="internal-content" />
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Primary Internal Person"
            label-class="font-weight-bold"
          >
            <b-form-select
              v-model="society.internal_person_id"
              :options="internalPersonOptions"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Quick Registration"
            label-class="font-weight-bold"
          >
            <div class="d-flex align-items-center h-100">
              <b-form-checkbox
                v-model="society.quick_registration"
                :value="1"
                :unchecked-value="0"
              />
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Membership Email:"
            label-class="font-weight-bold"
            :invalid-feedback="membershipEmailInvalidFeedback"
          >
            <b-form-input
              type="email"
              placeholder="Membership Email"
              v-model="$v.society.membership_email.$model"
              :state="validateState('membership_email')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="society.id" class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Society API Key:"
            label-class="font-weight-bold"
          >
            <b-row>
              <b-col cols="12">
                <b-form-input
                  readonly
                  type="text"
                  placeholder="Secret Key"
                  v-model="secretKey"
                />
              </b-col>
              <b-col class="mt-2">
                <b-button
                  v-if="isClipboardSupported() && secretKey"
                  variant="primary"
                  class="mr-2"
                  @click="copySecretKey"
                >
                  <i
                    class="mdi mdi-clipboard-text-multiple v2_font_size_12"
                    aria-hidden="true"
                  />
                </b-button>
                <b-button variant="primary" @click="onResetSecretKey">
                  <template v-if="!loadingSecretKey">
                    {{ secretKeyBtnName }}
                  </template>
                  <template v-else>
                    <b-spinner small />
                  </template>
                </b-button>
              </b-col>
            </b-row>
          </b-form-group>
        </b-col>
      </b-row>
      <register-unregistered-users
        v-if="society && society.id"
        :society-id="society.id"
      />
      <h4>Primary Contact</h4>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Primary Contact Name:"
            label-class="font-weight-bold"
            :invalid-feedback="textFieldInvalidFeedback"
          >
            <b-form-input
              type="text"
              placeholder="Primary Contact Name"
              v-model="$v.society.contact1_name.$model"
              :state="validateState('contact1_name')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Primary Contact Email:"
            label-class="font-weight-bold"
            :invalid-feedback="contact1EmailInvalidFeedback"
          >
            <b-form-input
              type="email"
              placeholder="Primary Contact Email"
              v-model="$v.society.contact1_email.$model"
              :state="validateState('contact1_email')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Primary Contact Phone:"
            label-class="font-weight-bold"
            :invalid-feedback="contact1PhoneInvalidFeedback"
          >
            <b-form-input
              type="text"
              placeholder="Primary Contact Phone"
              v-model="$v.society.contact1_phone.$model"
              :state="validateState('contact1_phone')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Primary Contact Role:"
            label-class="font-weight-bold"
            :invalid-feedback="textFieldInvalidFeedback"
          >
            <b-form-input
              type="text"
              placeholder="Primary Contact Role"
              v-model="$v.society.contact1_role.$model"
              :state="validateState('contact1_role')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <h4>Secondary Contact</h4>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Secondary Contact Name:"
            label-class="font-weight-bold"
            :invalid-feedback="textFieldInvalidFeedback"
          >
            <b-form-input
              type="text"
              placeholder="Secondary Contact Name"
              v-model="$v.society.contact2_name.$model"
              :state="validateState('contact2_name')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Secondary Contact Email:"
            label-class="font-weight-bold"
            :invalid-feedback="contact2EmailInvalidFeedback"
          >
            <b-form-input
              type="email"
              placeholder="Secondary Contact Email"
              v-model="$v.society.contact2_email.$model"
              :state="validateState('contact2_email')"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Secondary Contact Phone:"
            label-class="font-weight-bold"
            :invalid-feedback="contact2PhoneInvalidFeedback"
          >
            <b-form-input
              type="text"
              placeholder="Secondary Contact Phone"
              v-model="$v.society.contact2_phone.$model"
              :state="validateState('contact2_phone')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-3">
        <b-col sm="12">
          <b-form-group
            label-cols-horizontal
            label-cols="3"
            label="Secondary Contact Role:"
            label-class="font-weight-bold"
            :invalid-feedback="textFieldInvalidFeedback"
          >
            <b-form-input
              type="text"
              placeholder="Secondary Contact Role"
              v-model="$v.society.contact2_role.$model"
              :state="validateState('contact2_role')"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </b-container>
</template>

<script>
import Api from '@/providers/Api.js';
import { validationMixin } from 'vuelidate';
import {
  email,
  maxLength,
  minLength,
  required,
  helpers,
} from 'vuelidate/lib/validators';
import {
  PHONE_VALIDATION_MESSAGE,
  PHONE_VALIDATION_RGEXP,
} from '@/helpers/PhoneHelpers';
import SocietyLogoInput from '@/components/society/SocietyLogoInput';
import { fileExtensionValidator } from '@/helpers/customValidators';
import RegisterUnregisteredUsers from '@/components/society/RegisterUnregisteredUsers';
import { handleServerErrorWithValidation } from '@/helpers/ToastedHelper';
import Help from '@/providers/Help';

const phone = helpers.regex('phone', PHONE_VALIDATION_RGEXP);
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif'];

export default {
  name: 'SocietyForm',
  mixins: [validationMixin],
  components: {
    SocietyLogoInput,
    RegisterUnregisteredUsers,
  },
  props: {
    initialSociety: {
      type: Object,
      default: function () {
        return {};
      },
    },
    saveLoading: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      add_specialty_id: null,
      internalPersons: [],
      logoFile: null,
      loadingSecretKey: false,
      secretKey: this.initialSociety.secret_key || null,
      society: {
        id: this.initialSociety.id || null,
        name: this.initialSociety.name || '',
        url_code: this.initialSociety.url_code || '',
        contact1_name: this.initialSociety.contact1_name || '',
        contact1_email: this.initialSociety.contact1_email || '',
        contact1_phone: this.initialSociety.contact1_phone || '',
        contact1_role: this.initialSociety.contact1_role || '',
        contact2_name: this.initialSociety.contact2_name || '',
        contact2_email: this.initialSociety.contact2_email || '',
        contact2_phone: this.initialSociety.contact2_phone || '',
        contact2_role: this.initialSociety.contact2_role || '',
        society_email_signature:
          this.initialSociety.society_email_signature || '',
        logo: this.initialSociety.logo || '',
        logoFile: this.initialSociety.logoFile || null,
        quick_registration: this.initialSociety.quick_registration || 0,
        internal_person_id: this.initialSociety.internal_person_id || '',
        specialties: this.initialSociety.specialties || [],
        membership_email: this.initialSociety.membership_email || '',
        is_out_of_partners_list:
          this.initialSociety.is_out_of_partners_list || 0,
      },
    };
  },
  validations: {
    society: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(255),
      },
      url_code: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(32),
      },
      contact1_name: {
        maxLength: maxLength(32),
      },
      contact1_email: {
        email: email,
      },
      contact1_phone: {
        phone,
      },
      contact1_role: {
        maxLength: maxLength(32),
      },
      contact2_name: {
        maxLength: maxLength(32),
      },
      contact2_email: {
        email,
      },
      contact2_phone: {
        phone,
      },
      contact2_role: {
        maxLength: maxLength(32),
      },
      society_email_signature: {
        maxLength: maxLength(255),
      },
      membership_email: {
        email,
      },
    },
    logoFile: {
      fileExtensionValidator: fileExtensionValidator(ALLOWED_EXTENSIONS),
    },
  },
  created: function () {
    this.getInternalPersons();
  },
  computed: {
    title: function () {
      return this.society.id ? 'Edit Society' : 'Create Society';
    },
    specialtyOptions: function () {
      return this.$store.state.specialtyOptions;
    },
    internalPersonOptions: function () {
      return [
        { value: '', text: 'None' },
        ...this.internalPersons.map((person) => ({
          value: person.id,
          text: `${person.first_name} ${person.last_name}`,
        })),
      ];
    },
    logoSrc: function () {
      return this.logoFile instanceof File
        ? URL.createObjectURL(this.logoFile)
        : this.society.logo_url || this.society.logo;
    },
    nameInvalidFeedback: function () {
      if (!this.$v.society.name.maxLength) {
        return "Name can't be more than 255 characters.";
      }
      if (!this.$v.society.name.minLength) {
        return "Name can't be less than 3 characters.";
      }

      return 'This field is required';
    },
    urlCodeInvalidFeedback: function () {
      if (!this.$v.society.url_code.maxLength) {
        return "The Url Code can't be more than 32 characters.";
      }
      if (!this.$v.society.url_code.minLength) {
        return "The Url Code can't be less than 1 characters.";
      }

      return 'This field is required';
    },
    textFieldInvalidFeedback: function () {
      return "Field can't be more than 255 characters.";
    },
    contact1EmailInvalidFeedback: function () {
      return !this.$v.society.contact1_email.email
        ? 'Primary Contact Email must be a valid email.'
        : null;
    },
    contact2EmailInvalidFeedback: function () {
      return !this.$v.society.contact2_email.email
        ? 'Secondary Contact Email must be a valid email.'
        : null;
    },
    contact1PhoneInvalidFeedback: function () {
      return !this.$v.society.contact1_phone.phone
        ? PHONE_VALIDATION_MESSAGE
        : null;
    },
    contact2PhoneInvalidFeedback: function () {
      return !this.$v.society.contact2_phone.phone
        ? PHONE_VALIDATION_MESSAGE
        : null;
    },
    logoFileInvalidFeedback: function () {
      return `Only next file extensions are allowed: ${ALLOWED_EXTENSIONS.join()}`;
    },
    membershipEmailInvalidFeedback: function () {
      return 'Field must be a valid email address.';
    },
    secretKeyBtnName: function () {
      return this.secretKey ? 'Reset Secret key' : 'Create Secret key';
    },
  },
  methods: {
    deleteSpecialty: function (s) {
      const index = this.society.specialties.findIndex(
        (spec) => spec.id === s.id
      );
      if (index > -1) {
        this.$delete(this.society.specialties, index);
      }
    },
    addSpecialty: function (id) {
      if (
        this.society.specialties &&
        this.society.specialties.some(
          (specialty) => parseInt(specialty.id) === parseInt(id)
        )
      ) {
        this.$toasted.error('This specialty already added to the society', {
          duration: 3000,
        });
        this.add_specialty_id = null;
        return;
      }
      Api.getSpecialty(this, id).then((s) => {
        if (!Array.isArray(this.society.specialties)) {
          this.society.specialties = [];
        }
        this.society.specialties.push(s);
        this.add_specialty_id = null;
      });
    },
    cancel: function () {
      this.$emit('cancel');
    },
    submit: async function () {
      this.$v.$touch();
      if (this.$v.$anyError) {
        return;
      }
      const data = {
        ...this.society,
        specialties: JSON.stringify(this.society.specialties),
        logoFile: this.logoFile,
      };
      if (this.logoFile) {
        data.logo = '';
      } else if (this.society.logo) {
        data.logo_url = '';
      }
      this.$emit('form-submit', data);
      this.logoFile = '';
    },
    getInternalPersons: function () {
      Api.getActiveInternalPersons().then((persons) => {
        this.internalPersons = persons;
      });
    },
    validateState: function (name) {
      const { $dirty, $error } = this.$v.society[name];
      return $dirty ? !$error : null;
    },
    validateLogoFileState: function () {
      const { $dirty, $error } = this.$v.logoFile;
      return $dirty ? !$error : null;
    },
    onFileChange: function () {
      this.$v.logoFile.$touch();
    },
    onResetSecretKey: async function () {
      if (!this.society.id) {
        this.$toasted.info('Please save society before.', { duration: 3000 });
      }
      let message = 'Are you sure you want to reset society secret key?';
      if (!this.secretKey) {
        message = 'Are you sure you want to create society secret key?';
      }
      Help.confirm.show(message, 'Accept', () => {
        this.resetSecretKey();
      });
    },
    resetSecretKey: async function () {
      this.loadingSecretKey = true;
      try {
        const result = await Api.generateNewSecretKey(this.society.id);
        this.$set(this, 'secretKey', result);
      } catch (e) {
        handleServerErrorWithValidation(e, this.$toasted);
      } finally {
        this.loadingSecretKey = false;
      }
    },
    isClipboardSupported: function () {
      return (
        navigator &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function'
      );
    },
    copySecretKey: function () {
      if (!this.isClipboardSupported() || !this.secretKey) return;
      navigator.clipboard.writeText(this.secretKey);
      this.$toasted.success('Secret key copied successfully.', {
        duration: 3000,
      });
    },
  },
};
</script>

<style scoped />
